import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import Layout from './components/layout/Layout';
import ErrorBoundary from './components/ErrorBoundary';

// Home sections — 2026 redesign
import ParticleHero from './components/home/ParticleHero';
import Manifesto from './components/home/redesign/Manifesto';
import Capabilities from './components/home/redesign/Capabilities';
import Work from './components/home/redesign/Work';
import Process from './components/home/redesign/Process';
import WhyUs from './components/home/redesign/WhyUs';
import Testimonials from './components/home/redesign/Testimonials';
import TeamRoster from './components/home/redesign/TeamRoster';
import About from './components/home/redesign/About';
import FAQ from './components/home/redesign/FAQ';
import ContactTerminal from './components/home/redesign/ContactTerminal';
import PageBackdrop from './components/home/redesign/PageBackdrop';
import useReveal from './components/home/redesign/useReveal';
import ScrollProgress from './components/ui/ScrollProgress';
import SmartNavigator from './components/ui/SmartNavigator';
import PremiumScrollEffects from './components/ui/PremiumScrollEffects';
import { scrollToSection } from './utils/scrollToSection';

import './styles/redesign.css';

// Blog
import BlogPage from './components/home/BlogPage';
import BlogPost from './components/home/BlogPost';

// 404
import NotFound from './components/NotFound';

// Apps & Legal pages
import AppsPage from './components/apps/AppsPage';
import LabPage from './components/lab/LabPage';
import ClipPilotPrivacy from './components/apps/clippilot/PrivacyPolicy';
import ClipPilotTerms from './components/apps/clippilot/TermsConditions';
import ClipPilotSupport from './components/apps/clippilot/Support';
import HydrateDailyPrivacy from './components/apps/hydratedaily/PrivacyPolicy';
import HydrateDailyTerms from './components/apps/hydratedaily/TermsConditions';
import HydrateDailySupport from './components/apps/hydratedaily/Support';
import PrismaticaPrivacy from './components/apps/prismatica/PrivacyPolicy';
import PrismaticaTerms from './components/apps/prismatica/TermsConditions';
import PrismaticaSupport from './components/apps/prismatica/Support';
import TapLedgerPrivacy from './components/apps/tapledger/PrivacyPolicy';
import TapLedgerTerms from './components/apps/tapledger/TermsConditions';
import TapLedgerSupport from './components/apps/tapledger/Support';
import PackPalPrivacy from './components/apps/packpal/PrivacyPolicy';
import PackPalTerms from './components/apps/packpal/TermsConditions';
import PackPalSupport from './components/apps/packpal/Support';
import ClearcastPrivacy from './components/apps/clearcast/PrivacyPolicy';
import ClearcastTerms from './components/apps/clearcast/TermsConditions';
import ClearcastSupport from './components/apps/clearcast/Support';
import EnsemblePrivacy from './components/apps/ensemble/PrivacyPolicy';
import EnsembleTerms from './components/apps/ensemble/TermsConditions';
import EnsembleSupport from './components/apps/ensemble/Support';
import OpenClawPrivacy from './components/apps/openclaw/PrivacyPolicy';
import OpenClawTerms from './components/apps/openclaw/TermsConditions';
import BlocPrivacy from './components/apps/bloc/PrivacyPolicy';
import BlocTerms from './components/apps/bloc/TermsConditions';
import BlocSupport from './components/apps/bloc/Support';
import BouncerPrivacy from './components/apps/bouncer/PrivacyPolicy';
import BouncerTerms from './components/apps/bouncer/TermsConditions';
import BouncerSupport from './components/apps/bouncer/Support';
import ClaimlyPrivacy from './components/apps/claimly/PrivacyPolicy';
import ClaimlyTerms from './components/apps/claimly/TermsConditions';
import ClaimlySupport from './components/apps/claimly/Support';
import CuePrivacy from './components/apps/cue/PrivacyPolicy';
import CueTerms from './components/apps/cue/TermsConditions';
import CueSupport from './components/apps/cue/Support';
import DriftPrivacy from './components/apps/drift/PrivacyPolicy';
import DriftTerms from './components/apps/drift/TermsConditions';
import DriftSupport from './components/apps/drift/Support';
import GaugePrivacy from './components/apps/gauge/PrivacyPolicy';
import GaugeTerms from './components/apps/gauge/TermsConditions';
import GaugeSupport from './components/apps/gauge/Support';
import HushPrivacy from './components/apps/hush/PrivacyPolicy';
import HushTerms from './components/apps/hush/TermsConditions';
import HushSupport from './components/apps/hush/Support';
import HutchPrivacy from './components/apps/hutch/PrivacyPolicy';
import HutchTerms from './components/apps/hutch/TermsConditions';
import HutchSupport from './components/apps/hutch/Support';
import InflectPrivacy from './components/apps/inflect/PrivacyPolicy';
import InflectTerms from './components/apps/inflect/TermsConditions';
import InflectSupport from './components/apps/inflect/Support';
import LockInPrivacy from './components/apps/lockin/PrivacyPolicy';
import LockInTerms from './components/apps/lockin/TermsConditions';
import LockInSupport from './components/apps/lockin/Support';
import LorePrivacy from './components/apps/lore/PrivacyPolicy';
import LoreTerms from './components/apps/lore/TermsConditions';
import LoreSupport from './components/apps/lore/Support';
import MendPrivacy from './components/apps/mend/PrivacyPolicy';
import MendTerms from './components/apps/mend/TermsConditions';
import MendSupport from './components/apps/mend/Support';
import MeritPrivacy from './components/apps/merit/PrivacyPolicy';
import MeritTerms from './components/apps/merit/TermsConditions';
import MeritSupport from './components/apps/merit/Support';
import NuzzlePrivacy from './components/apps/nuzzle/PrivacyPolicy';
import NuzzleTerms from './components/apps/nuzzle/TermsConditions';
import NuzzleSupport from './components/apps/nuzzle/Support';
import ProoflyPrivacy from './components/apps/proofly/PrivacyPolicy';
import ProoflyTerms from './components/apps/proofly/TermsConditions';
import ProoflySupport from './components/apps/proofly/Support';
import ProvisoPrivacy from './components/apps/proviso/PrivacyPolicy';
import ProvisoTerms from './components/apps/proviso/TermsConditions';
import ProvisoSupport from './components/apps/proviso/Support';
import QuorumPrivacy from './components/apps/quorum/PrivacyPolicy';
import QuorumTerms from './components/apps/quorum/TermsConditions';
import QuorumSupport from './components/apps/quorum/Support';
import ResonancePrivacy from './components/apps/resonance/PrivacyPolicy';
import ResonanceTerms from './components/apps/resonance/TermsConditions';
import ResonanceSupport from './components/apps/resonance/Support';
import RipePrivacy from './components/apps/ripe/PrivacyPolicy';
import RipeTerms from './components/apps/ripe/TermsConditions';
import RipeSupport from './components/apps/ripe/Support';
import StancePrivacy from './components/apps/stance/PrivacyPolicy';
import StanceTerms from './components/apps/stance/TermsConditions';
import StanceSupport from './components/apps/stance/Support';
import SteadyPrivacy from './components/apps/steady/PrivacyPolicy';
import SteadyTerms from './components/apps/steady/TermsConditions';
import SteadySupport from './components/apps/steady/Support';
import StillwayPrivacy from './components/apps/stillway/PrivacyPolicy';
import StillwayTerms from './components/apps/stillway/TermsConditions';
import StillwaySupport from './components/apps/stillway/Support';
import SynapsePrivacy from './components/apps/synapse/PrivacyPolicy';
import SynapseTerms from './components/apps/synapse/TermsConditions';
import SynapseSupport from './components/apps/synapse/Support';
import TetherPrivacy from './components/apps/tether/PrivacyPolicy';
import TetherTerms from './components/apps/tether/TermsConditions';
import TetherSupport from './components/apps/tether/Support';
import UnearthPrivacy from './components/apps/unearth/PrivacyPolicy';
import UnearthTerms from './components/apps/unearth/TermsConditions';
import UnearthSupport from './components/apps/unearth/Support';
import UpkeepPrivacy from './components/apps/upkeep/PrivacyPolicy';
import UpkeepTerms from './components/apps/upkeep/TermsConditions';
import UpkeepSupport from './components/apps/upkeep/Support';
import VerdictPrivacy from './components/apps/verdict/PrivacyPolicy';
import VerdictTerms from './components/apps/verdict/TermsConditions';
import VerdictSupport from './components/apps/verdict/Support';
import TomorrowBoardPrivacy from './components/apps/tomorrowboard/PrivacyPolicy';
import TomorrowBoardTerms from './components/apps/tomorrowboard/TermsConditions';
import TomorrowBoardSupport from './components/apps/tomorrowboard/Support';
import PeriSignalPrivacy from './components/apps/perisignal/PrivacyPolicy';
import PeriSignalTerms from './components/apps/perisignal/TermsConditions';
import PeriSignalSupport from './components/apps/perisignal/Support';
import AfterDosePrivacy from './components/apps/afterdose/PrivacyPolicy';
import AfterDoseTerms from './components/apps/afterdose/TermsConditions';
import AfterDoseSupport from './components/apps/afterdose/Support';
import HandrailPrivacy from './components/apps/handrail/PrivacyPolicy';
import HandrailTerms from './components/apps/handrail/TermsConditions';
import HandrailSupport from './components/apps/handrail/Support';
import StowryPrivacy from './components/apps/stowry/PrivacyPolicy';
import StowryTerms from './components/apps/stowry/TermsConditions';
import StowrySupport from './components/apps/stowry/Support';
import FrontPorchPrivacy from './components/apps/frontporch/PrivacyPolicy';
import FrontPorchTerms from './components/apps/frontporch/TermsConditions';
import FrontPorchSupport from './components/apps/frontporch/Support';
import KinRelayPrivacy from './components/apps/kinrelay/PrivacyPolicy';
import KinRelayTerms from './components/apps/kinrelay/TermsConditions';
import KinRelaySupport from './components/apps/kinrelay/Support';

import ResizeObserverHandler from './utils/ResizeObserverHandler';
import usePremiumMotion from './hooks/usePremiumMotion';

// Theme context
import { ThemeProvider } from './context/ThemeContext';

// Lazy-loaded heavy components
const GamesNavButton = React.lazy(() => import('./components/GamesNavButton'));

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const PageTransition = ({ children }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
  >
    {children}
  </motion.div>
);

const HomePage = () => {
  useReveal();
  useEffect(() => {
    const { hash } = window.location;
    if (!hash || hash === '#') return undefined;
    const t = setTimeout(() => scrollToSection(hash), 350);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="r-root">
      <PageBackdrop />
      <ParticleHero />
      <Manifesto />
      <Capabilities />
      <Work />
      <Process />
      <WhyUs />
      <Testimonials />
      <TeamRoster />
      <About />
      <FAQ />
      <ContactTerminal />
    </div>
  );
};

const AnimatedRoutes = () => {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageTransition><HomePage /></PageTransition>} />
        <Route path="/blog" element={<PageTransition><BlogPage /></PageTransition>} />
        <Route path="/blog/:postId" element={<PageTransition><BlogPost /></PageTransition>} />
        <Route path="/apps" element={<PageTransition><AppsPage /></PageTransition>} />
        <Route path="/lab" element={<PageTransition><LabPage /></PageTransition>} />
        <Route path="/apps/clippilot/privacy" element={<PageTransition><ClipPilotPrivacy /></PageTransition>} />
        <Route path="/apps/clippilot/terms" element={<PageTransition><ClipPilotTerms /></PageTransition>} />
        <Route path="/apps/clippilot/support" element={<PageTransition><ClipPilotSupport /></PageTransition>} />
        <Route path="/apps/hydratedaily/privacy" element={<PageTransition><HydrateDailyPrivacy /></PageTransition>} />
        <Route path="/apps/hydratedaily/terms" element={<PageTransition><HydrateDailyTerms /></PageTransition>} />
        <Route path="/apps/hydratedaily/support" element={<PageTransition><HydrateDailySupport /></PageTransition>} />
        <Route path="/apps/prismatica/privacy" element={<PageTransition><PrismaticaPrivacy /></PageTransition>} />
        <Route path="/apps/prismatica/terms" element={<PageTransition><PrismaticaTerms /></PageTransition>} />
        <Route path="/apps/prismatica/support" element={<PageTransition><PrismaticaSupport /></PageTransition>} />
        <Route path="/apps/tapledger/privacy" element={<PageTransition><TapLedgerPrivacy /></PageTransition>} />
        <Route path="/apps/tapledger/terms" element={<PageTransition><TapLedgerTerms /></PageTransition>} />
        <Route path="/apps/tapledger/support" element={<PageTransition><TapLedgerSupport /></PageTransition>} />
        <Route path="/apps/packpal/privacy" element={<PageTransition><PackPalPrivacy /></PageTransition>} />
        <Route path="/apps/packpal/terms" element={<PageTransition><PackPalTerms /></PageTransition>} />
        <Route path="/apps/packpal/support" element={<PageTransition><PackPalSupport /></PageTransition>} />
        <Route path="/apps/clearcast/privacy" element={<PageTransition><ClearcastPrivacy /></PageTransition>} />
        <Route path="/apps/clearcast/terms" element={<PageTransition><ClearcastTerms /></PageTransition>} />
        <Route path="/apps/clearcast/support" element={<PageTransition><ClearcastSupport /></PageTransition>} />
        <Route path="/apps/ensemble/privacy" element={<PageTransition><EnsemblePrivacy /></PageTransition>} />
        <Route path="/apps/ensemble/terms" element={<PageTransition><EnsembleTerms /></PageTransition>} />
        <Route path="/apps/ensemble/support" element={<PageTransition><EnsembleSupport /></PageTransition>} />
        <Route path="/apps/openclaw/privacy" element={<PageTransition><OpenClawPrivacy /></PageTransition>} />
        <Route path="/apps/openclaw/terms" element={<PageTransition><OpenClawTerms /></PageTransition>} />
        <Route path="/apps/bloc/privacy" element={<PageTransition><BlocPrivacy /></PageTransition>} />
        <Route path="/apps/bloc/terms" element={<PageTransition><BlocTerms /></PageTransition>} />
        <Route path="/apps/bloc/support" element={<PageTransition><BlocSupport /></PageTransition>} />
        <Route path="/apps/bouncer/privacy" element={<PageTransition><BouncerPrivacy /></PageTransition>} />
        <Route path="/apps/bouncer/terms" element={<PageTransition><BouncerTerms /></PageTransition>} />
        <Route path="/apps/bouncer/support" element={<PageTransition><BouncerSupport /></PageTransition>} />
        <Route path="/apps/claimly/privacy" element={<PageTransition><ClaimlyPrivacy /></PageTransition>} />
        <Route path="/apps/claimly/terms" element={<PageTransition><ClaimlyTerms /></PageTransition>} />
        <Route path="/apps/claimly/support" element={<PageTransition><ClaimlySupport /></PageTransition>} />
        <Route path="/apps/cue/privacy" element={<PageTransition><CuePrivacy /></PageTransition>} />
        <Route path="/apps/cue/terms" element={<PageTransition><CueTerms /></PageTransition>} />
        <Route path="/apps/cue/support" element={<PageTransition><CueSupport /></PageTransition>} />
        <Route path="/apps/drift/privacy" element={<PageTransition><DriftPrivacy /></PageTransition>} />
        <Route path="/apps/drift/terms" element={<PageTransition><DriftTerms /></PageTransition>} />
        <Route path="/apps/drift/support" element={<PageTransition><DriftSupport /></PageTransition>} />
        <Route path="/apps/gauge/privacy" element={<PageTransition><GaugePrivacy /></PageTransition>} />
        <Route path="/apps/gauge/terms" element={<PageTransition><GaugeTerms /></PageTransition>} />
        <Route path="/apps/gauge/support" element={<PageTransition><GaugeSupport /></PageTransition>} />
        <Route path="/apps/hush/privacy" element={<PageTransition><HushPrivacy /></PageTransition>} />
        <Route path="/apps/hush/terms" element={<PageTransition><HushTerms /></PageTransition>} />
        <Route path="/apps/hush/support" element={<PageTransition><HushSupport /></PageTransition>} />
        <Route path="/apps/hutch/privacy" element={<PageTransition><HutchPrivacy /></PageTransition>} />
        <Route path="/apps/hutch/terms" element={<PageTransition><HutchTerms /></PageTransition>} />
        <Route path="/apps/hutch/support" element={<PageTransition><HutchSupport /></PageTransition>} />
        <Route path="/apps/inflect/privacy" element={<PageTransition><InflectPrivacy /></PageTransition>} />
        <Route path="/apps/inflect/terms" element={<PageTransition><InflectTerms /></PageTransition>} />
        <Route path="/apps/inflect/support" element={<PageTransition><InflectSupport /></PageTransition>} />
        <Route path="/apps/lockin/privacy" element={<PageTransition><LockInPrivacy /></PageTransition>} />
        <Route path="/apps/lockin/terms" element={<PageTransition><LockInTerms /></PageTransition>} />
        <Route path="/apps/lockin/support" element={<PageTransition><LockInSupport /></PageTransition>} />
        <Route path="/apps/lore/privacy" element={<PageTransition><LorePrivacy /></PageTransition>} />
        <Route path="/apps/lore/terms" element={<PageTransition><LoreTerms /></PageTransition>} />
        <Route path="/apps/lore/support" element={<PageTransition><LoreSupport /></PageTransition>} />
        <Route path="/apps/mend/privacy" element={<PageTransition><MendPrivacy /></PageTransition>} />
        <Route path="/apps/mend/terms" element={<PageTransition><MendTerms /></PageTransition>} />
        <Route path="/apps/mend/support" element={<PageTransition><MendSupport /></PageTransition>} />
        <Route path="/apps/merit/privacy" element={<PageTransition><MeritPrivacy /></PageTransition>} />
        <Route path="/apps/merit/terms" element={<PageTransition><MeritTerms /></PageTransition>} />
        <Route path="/apps/merit/support" element={<PageTransition><MeritSupport /></PageTransition>} />
        <Route path="/apps/nuzzle/privacy" element={<PageTransition><NuzzlePrivacy /></PageTransition>} />
        <Route path="/apps/nuzzle/terms" element={<PageTransition><NuzzleTerms /></PageTransition>} />
        <Route path="/apps/nuzzle/support" element={<PageTransition><NuzzleSupport /></PageTransition>} />
        <Route path="/apps/proofly/privacy" element={<PageTransition><ProoflyPrivacy /></PageTransition>} />
        <Route path="/apps/proofly/terms" element={<PageTransition><ProoflyTerms /></PageTransition>} />
        <Route path="/apps/proofly/support" element={<PageTransition><ProoflySupport /></PageTransition>} />
        <Route path="/apps/proviso/privacy" element={<PageTransition><ProvisoPrivacy /></PageTransition>} />
        <Route path="/apps/proviso/terms" element={<PageTransition><ProvisoTerms /></PageTransition>} />
        <Route path="/apps/proviso/support" element={<PageTransition><ProvisoSupport /></PageTransition>} />
        <Route path="/apps/quorum/privacy" element={<PageTransition><QuorumPrivacy /></PageTransition>} />
        <Route path="/apps/quorum/terms" element={<PageTransition><QuorumTerms /></PageTransition>} />
        <Route path="/apps/quorum/support" element={<PageTransition><QuorumSupport /></PageTransition>} />
        <Route path="/apps/resonance/privacy" element={<PageTransition><ResonancePrivacy /></PageTransition>} />
        <Route path="/apps/resonance/terms" element={<PageTransition><ResonanceTerms /></PageTransition>} />
        <Route path="/apps/resonance/support" element={<PageTransition><ResonanceSupport /></PageTransition>} />
        <Route path="/apps/ripe/privacy" element={<PageTransition><RipePrivacy /></PageTransition>} />
        <Route path="/apps/ripe/terms" element={<PageTransition><RipeTerms /></PageTransition>} />
        <Route path="/apps/ripe/support" element={<PageTransition><RipeSupport /></PageTransition>} />
        <Route path="/apps/stance/privacy" element={<PageTransition><StancePrivacy /></PageTransition>} />
        <Route path="/apps/stance/terms" element={<PageTransition><StanceTerms /></PageTransition>} />
        <Route path="/apps/stance/support" element={<PageTransition><StanceSupport /></PageTransition>} />
        <Route path="/apps/steady/privacy" element={<PageTransition><SteadyPrivacy /></PageTransition>} />
        <Route path="/apps/steady/terms" element={<PageTransition><SteadyTerms /></PageTransition>} />
        <Route path="/apps/steady/support" element={<PageTransition><SteadySupport /></PageTransition>} />
        <Route path="/apps/stillway/privacy" element={<PageTransition><StillwayPrivacy /></PageTransition>} />
        <Route path="/apps/stillway/terms" element={<PageTransition><StillwayTerms /></PageTransition>} />
        <Route path="/apps/stillway/support" element={<PageTransition><StillwaySupport /></PageTransition>} />
        <Route path="/apps/synapse/privacy" element={<PageTransition><SynapsePrivacy /></PageTransition>} />
        <Route path="/apps/synapse/terms" element={<PageTransition><SynapseTerms /></PageTransition>} />
        <Route path="/apps/synapse/support" element={<PageTransition><SynapseSupport /></PageTransition>} />
        <Route path="/apps/tether/privacy" element={<PageTransition><TetherPrivacy /></PageTransition>} />
        <Route path="/apps/tether/terms" element={<PageTransition><TetherTerms /></PageTransition>} />
        <Route path="/apps/tether/support" element={<PageTransition><TetherSupport /></PageTransition>} />
        <Route path="/apps/unearth/privacy" element={<PageTransition><UnearthPrivacy /></PageTransition>} />
        <Route path="/apps/unearth/terms" element={<PageTransition><UnearthTerms /></PageTransition>} />
        <Route path="/apps/unearth/support" element={<PageTransition><UnearthSupport /></PageTransition>} />
        <Route path="/apps/upkeep/privacy" element={<PageTransition><UpkeepPrivacy /></PageTransition>} />
        <Route path="/apps/upkeep/terms" element={<PageTransition><UpkeepTerms /></PageTransition>} />
        <Route path="/apps/upkeep/support" element={<PageTransition><UpkeepSupport /></PageTransition>} />
        <Route path="/apps/verdict/privacy" element={<PageTransition><VerdictPrivacy /></PageTransition>} />
        <Route path="/apps/verdict/terms" element={<PageTransition><VerdictTerms /></PageTransition>} />
        <Route path="/apps/verdict/support" element={<PageTransition><VerdictSupport /></PageTransition>} />
        <Route path="/apps/tomorrowboard/privacy" element={<PageTransition><TomorrowBoardPrivacy /></PageTransition>} />
        <Route path="/apps/tomorrowboard/terms" element={<PageTransition><TomorrowBoardTerms /></PageTransition>} />
        <Route path="/apps/tomorrowboard/support" element={<PageTransition><TomorrowBoardSupport /></PageTransition>} />
        <Route path="/apps/perisignal/privacy" element={<PageTransition><PeriSignalPrivacy /></PageTransition>} />
        <Route path="/apps/perisignal/terms" element={<PageTransition><PeriSignalTerms /></PageTransition>} />
        <Route path="/apps/perisignal/support" element={<PageTransition><PeriSignalSupport /></PageTransition>} />
        <Route path="/apps/afterdose/privacy" element={<PageTransition><AfterDosePrivacy /></PageTransition>} />
        <Route path="/apps/afterdose/terms" element={<PageTransition><AfterDoseTerms /></PageTransition>} />
        <Route path="/apps/afterdose/support" element={<PageTransition><AfterDoseSupport /></PageTransition>} />
        <Route path="/apps/handrail/privacy" element={<PageTransition><HandrailPrivacy /></PageTransition>} />
        <Route path="/apps/handrail/terms" element={<PageTransition><HandrailTerms /></PageTransition>} />
        <Route path="/apps/handrail/support" element={<PageTransition><HandrailSupport /></PageTransition>} />
        <Route path="/apps/stowry/privacy" element={<PageTransition><StowryPrivacy /></PageTransition>} />
        <Route path="/apps/stowry/terms" element={<PageTransition><StowryTerms /></PageTransition>} />
        <Route path="/apps/stowry/support" element={<PageTransition><StowrySupport /></PageTransition>} />
        <Route path="/apps/frontporch/privacy" element={<PageTransition><FrontPorchPrivacy /></PageTransition>} />
        <Route path="/apps/frontporch/terms" element={<PageTransition><FrontPorchTerms /></PageTransition>} />
        <Route path="/apps/frontporch/support" element={<PageTransition><FrontPorchSupport /></PageTransition>} />
        <Route path="/apps/kinrelay/privacy" element={<PageTransition><KinRelayPrivacy /></PageTransition>} />
        <Route path="/apps/kinrelay/terms" element={<PageTransition><KinRelayTerms /></PageTransition>} />
        <Route path="/apps/kinrelay/support" element={<PageTransition><KinRelaySupport /></PageTransition>} />
        <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
      </Routes>
    </AnimatePresence>
  );
};

const SiteChrome = () => {
  return (
    <>
      <ScrollProgress />
      <PremiumScrollEffects />
      <SmartNavigator />
      <React.Suspense fallback={null}>
        <GamesNavButton />
      </React.Suspense>
    </>
  );
};

function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [currentTheme, setCurrentTheme] = useState(localStorage.getItem('theme') || 'dark');

  usePremiumMotion();

  useEffect(() => {
    document.documentElement.classList.remove('dark-theme', 'light-theme');
    document.documentElement.classList.add(`${currentTheme}-theme`);
    const timer = setTimeout(() => setIsLoading(false), 400);
    return () => clearTimeout(timer);
  }, [currentTheme]);

  if (isLoading) {
    return (
      <div
        className="fixed inset-0 flex items-center justify-center z-50 overflow-hidden"
        style={{ background: 'var(--bg-primary)' }}
      >
        <div className="premium-loader-orb" />
        <div className="premium-loader-grid" />
        <div className="text-center relative z-10 px-6">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          >
            <motion.img
              src="/logo1.svg"
              alt="Feral Techlabs"
              className="w-56 mx-auto mb-6"
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: [1, 1.04, 1] }}
              transition={{
                opacity: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
                scale: { duration: 2.2, ease: 'easeInOut', repeat: Infinity },
              }}
            />
            <div className="premium-loader-copy">Engineering the unfair advantage</div>
            <div className="premium-loader-bar">
              <motion.div
                className="premium-loader-bar-fill"
                initial={{ scaleX: 0.15 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 1.5, ease: [0.16, 1, 0.3, 1] }}
              />

              <motion.div
                className="premium-loader-particles"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3, duration: 0.8 }}
              >
                {[...Array(12)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="loader-particle"
                    initial={{ scale: 0, rotate: 0 }}
                    animate={{
                      scale: [0, 1, 0],
                      rotate: 360,
                      opacity: [0, 1, 0],
                    }}
                    transition={{
                      duration: 2,
                      delay: i * 0.1,
                      repeat: Infinity,
                      ease: 'easeInOut',
                    }}
                    style={{
                      left: `${50 + 30 * Math.cos((i * 30 * Math.PI) / 180)}%`,
                      top: `${50 + 30 * Math.sin((i * 30 * Math.PI) / 180)}%`,
                    }}
                  />
                ))}
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <ThemeProvider
        value={{
          theme: currentTheme,
          setTheme: (theme) => {
            localStorage.setItem('theme', theme);
            document.documentElement.classList.remove('dark-theme', 'light-theme');
            document.documentElement.classList.add(`${theme}-theme`);
            setCurrentTheme(theme);
          },
        }}
      >
        <ResizeObserverHandler />
        <BrowserRouter>
          <ScrollToTop />
          <SiteChrome />
          <Layout>
            <AnimatedRoutes />
          </Layout>
        </BrowserRouter>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
